import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { consumeRedirect } from '../utils/postLoginRedirect';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Avatar,
  IconButton,
  InputAdornment,
  useTheme,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Visibility, VisibilityOff, DarkMode, LightMode } from '@mui/icons-material';
import logoLight from '../FF_Logo_50X180px-01 (1).png';
import logoDark from '../FF_Logo_50X180px-02 (1).png';

const Login = () => {
  const navigate = useNavigate();
  const { login, loginMicrosoft } = useAuth();
  const theme = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [darkMode, setDarkMode] = useState(theme.palette.mode === 'dark');

  // Initialize theme on component mount
  useEffect(() => {
    const isDark = theme.palette.mode === 'dark';
    setDarkMode(isDark);
    if (isDark) {
      document.body.classList.add('dark-mode');
      document.body.classList.remove('light-mode');
    } else {
      document.body.classList.add('light-mode');
      document.body.classList.remove('dark-mode');
    }
  }, [theme.palette.mode]);

  // Create a local theme object based on darkMode state
  const localTheme = {
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#FF6B35' : '#1976d2',
      },
      background: {
        default: darkMode ? '#2C3034' : '#FAFAFA',
        paper: darkMode ? 'rgba(44, 48, 52, 0.3)' : 'rgba(255, 255, 255, 0.3)',
      },
      text: {
        primary: darkMode ? 'white' : '#2C3034',
        secondary: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(44, 48, 52, 0.7)',
      },
    },
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleThemeToggle = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    
    // Update body classes for background
    if (newDarkMode) {
      document.body.classList.add('dark-mode');
      document.body.classList.remove('light-mode');
    } else {
      document.body.classList.add('light-mode');
      document.body.classList.remove('dark-mode');
    }
    
    // Force re-render by updating the theme context
    // This is a workaround since we don't have direct access to theme provider
    window.dispatchEvent(new CustomEvent('themeToggle', { detail: { darkMode: newDarkMode } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      console.log(formData.password);
      const loginResponse = await login(formData.email, formData.password);
      // Go back to where they were heading (e.g. an approval link), else by role.
      const redirectPath = consumeRedirect() || (loginResponse?.user?.role === 'partner' ? '/tasks' : '/dashboard');
      navigate(redirectPath);
    } catch (error) {
      setError(error.message || 'Failed to login. Please check your credentials.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: darkMode 
          ? 'linear-gradient(135deg, #2C3034 0%, #1a1a2e 100%)'
          : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("/watermark-icon.webp") no-repeat center center',
          backgroundSize: '80%',
          opacity: darkMode ? 0.25 : 0.18,
          zIndex: 0,
          pointerEvents: 'none',
        }
      }}
    >
      {/* Theme Toggle Button - Fixed Position */}
      <Box sx={{ 
        position: 'fixed', 
        top: 20, 
        right: 20, 
        zIndex: 1000,
        backgroundColor: darkMode 
          ? 'rgba(44, 48, 52, 0.4)' 
          : 'rgba(255, 255, 255, 0.4)',
        borderRadius: 2,
        padding: 1,
        boxShadow: darkMode 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 4px 20px rgba(0, 0, 0, 0.1)',
        border: darkMode 
          ? '1px solid rgba(255, 255, 255, 0.1)' 
          : '1px solid rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(10px)'
      }}>
        <FormControlLabel
          control={
            <Switch
              checked={darkMode}
              onChange={handleThemeToggle}
              icon={<LightMode />}
              checkedIcon={<DarkMode />}
              sx={{
                '& .MuiSwitch-thumb': {
                  backgroundColor: darkMode ? '#FF6B35' : '#1976d2',
                },
                '& .MuiSwitch-track': {
                  backgroundColor: darkMode ? 'rgba(255, 107, 53, 0.3)' : 'rgba(25, 118, 210, 0.3)',
                },
              }}
            />
          }
          label=""
            sx={{ 
              margin: 0,
              '& .MuiFormControlLabel-label': { 
                color: localTheme.palette.text.primary,
                fontWeight: 600
              } 
            }}
        />
      </Box>

      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
             <Paper
               elevation={8}
               sx={{
                 padding: { xs: 3, sm: 4, md: 5 },
                 display: 'flex',
                 flexDirection: 'column',
                 alignItems: 'center',
                 width: '100%',
                 maxWidth: 500,
                 borderRadius: 3,
                 background: darkMode 
                   ? 'rgba(44, 48, 52, 0.3)' 
                   : 'rgba(255, 255, 255, 0.3)',
                 backdropFilter: 'blur(6px) saturate(120%)',
                 border: darkMode 
                   ? '1px solid rgba(255, 255, 255, 0.1)' 
                   : '1px solid rgba(255, 255, 255, 0.12)',
                 boxShadow: darkMode 
                   ? '0 15px 30px rgba(0, 0, 0, 0.3), 0 6px 12px rgba(0, 0, 0, 0.2)'
                   : '0 15px 30px rgba(0, 0, 0, 0.06), 0 6px 12px rgba(0, 0, 0, 0.04)',
               }}
             >
           {/* FlatForm Logo */}
           <Avatar
             src={darkMode ? logoDark : logoLight}
             alt="FlatForm Logo"
             variant="square"
             sx={{
                width: 450,
                height: 250,
               mb: 0,
               //boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
               borderRadius: 2,
               '& img': {
                 objectFit: 'contain',
                 width: '100%',
                 height: '100%',
               }
             }}
           />
           
           {/* Welcome Message */}
           
           <Typography component="h2" variant="h6" sx={{ 
             mt: 0, mb: 0,
             color: localTheme.palette.text.primary,
             fontWeight: 600,
             textShadow: darkMode 
               ? '0 0 6px rgba(0, 0, 0, 0.6)' 
               : '0 0 6px rgba(255, 255, 255, 0.6)'
           }}>
             Sign in to your account
           </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
           <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, width: '100%' }}>
             <TextField
               margin="normal"
               required
               fullWidth
               id="email"
               label="Email Address"
               name="email"
               autoComplete="email"
               autoFocus
               value={formData.email}
               onChange={handleChange}
               sx={{ 
                 mb: 2,
                 '& .MuiInputLabel-root': {
                   color: localTheme.palette.text.primary,
                   fontWeight: 600,
                   textShadow: darkMode 
                     ? '0 0 4px rgba(0, 0, 0, 0.5)' 
                     : '0 0 4px rgba(255, 255, 255, 0.5)'
                 },
                 '& .MuiOutlinedInput-root': {
                   '& fieldset': {
                     borderColor: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                   },
                   '&:hover fieldset': {
                     borderColor: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                   },
                   '&.Mui-focused fieldset': {
                     borderColor: localTheme.palette.primary.main,
                   },
                 },
                 '& .MuiOutlinedInput-input': {
                   color: localTheme.palette.text.primary,
                   textShadow: darkMode 
                     ? '0 0 4px rgba(0, 0, 0, 0.5)' 
                     : '0 0 4px rgba(255, 255, 255, 0.5)'
                 }
               }}
             />
               <TextField
                 margin="normal"
                 required
                 fullWidth
                 name="password"
                 label="Password"
                 type={showPassword ? 'text' : 'password'}
                 id="password"
                 autoComplete="current-password"
                 value={formData.password}
                 onChange={handleChange}
                 sx={{ 
                   mb: 2,
                   '& .MuiInputLabel-root': {
                     color: localTheme.palette.text.primary,
                     fontWeight: 600,
                     textShadow: darkMode 
                       ? '0 0 4px rgba(0, 0, 0, 0.5)' 
                       : '0 0 4px rgba(255, 255, 255, 0.5)'
                   },
                   '& .MuiOutlinedInput-root': {
                     '& fieldset': {
                       borderColor: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                     },
                     '&:hover fieldset': {
                       borderColor: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                     },
                     '&.Mui-focused fieldset': {
                       borderColor: localTheme.palette.primary.main,
                     },
                   },
                   '& .MuiOutlinedInput-input': {
                     color: localTheme.palette.text.primary,
                     textShadow: darkMode 
                       ? '0 0 4px rgba(0, 0, 0, 0.5)' 
                       : '0 0 4px rgba(255, 255, 255, 0.5)'
                   }
                 }}
                 InputProps={{
                   endAdornment: (
                     <InputAdornment position="end">
                       <IconButton
                         aria-label="toggle password visibility"
                         onClick={handleClickShowPassword}
                         edge="end"
                         sx={{ color: localTheme.palette.text.secondary }}
                       >
                         {showPassword ? <VisibilityOff /> : <Visibility />}
                       </IconButton>
                     </InputAdornment>
                   ),
                 }}
               />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2,
                backgroundColor: localTheme.palette.primary.main,
                '&:hover': {
                  backgroundColor: darkMode ? '#e55a2b' : '#1565c0',
                },
                fontWeight: 600
              }}
            >
              Sign In
            </Button>
            
            <Box sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ 
                color: localTheme.palette.text.secondary,
                fontWeight: 600
              }}>
                OR
              </Typography>
            </Box>
            
            <Button
              type="button"
              fullWidth
              variant="outlined"
              onClick={loginMicrosoft}
              sx={{ 
                mt: 1, 
                mb: 2,
                border: darkMode 
                  ? '1px solid rgba(255, 255, 255, 0.3)' 
                  : '1px solid #e6ece7',
                backgroundColor: darkMode 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : '#fff',
                color: localTheme.palette.text.primary,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                '&:hover': {
                  backgroundColor: darkMode 
                    ? 'rgba(255, 255, 255, 0.2)' 
                    : '#f5f5f5',
                  border: darkMode 
                    ? '1px solid rgba(255, 255, 255, 0.5)' 
                    : '1px solid #d0d0d0'
                }
              }}
              startIcon={
                <div style={{
                  display: 'flex',
                  width: '20px',
                  height: '20px',
                  flexWrap: 'wrap',
                  gap: '1px'
                }}>
                  <div style={{ width: '9px', height: '9px', backgroundColor: '#F25022' }}></div>
                  <div style={{ width: '9px', height: '9px', backgroundColor: '#7FBA00' }}></div>
                  <div style={{ width: '9px', height: '9px', backgroundColor: '#00A4EF' }}></div>
                  <div style={{ width: '9px', height: '9px', backgroundColor: '#FFB900' }}></div>
                </div>
              }
            >
              Sign in with Microsoft
            </Button>
           </Box>
         </Paper>
       </Box>
     </Container>
    </Box>
   );
 };

export default Login; 